"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.Button = void 0;
const react_1 = __importStar(require("react"));
const hooks_1 = require("../../../hooks");
const mergeRefs_1 = require("../../utils/mergeRefs");
const Box_1 = require("../Box");
const Spinner_1 = require("../Spinner");
const Button_styles_1 = require("./Button.styles");
const ButtonContext = (0, react_1.createContext)({
    buttonAppearance: "primary",
    buttonDisabled: false,
});
const ButtonRoot = (0, react_1.forwardRef)((_a, forwardedRef) => {
    var { children, appearance = "primary", size = "medium", disabled = false, loading = false, loadingMessage, fullwidth = false } = _a, props = __rest(_a, ["children", "appearance", "size", "disabled", "loading", "loadingMessage", "fullwidth"]);
    const buttonRef = (0, react_1.useRef)(null);
    const [content, setContent] = (0, react_1.useState)(children);
    const viewport = (0, hooks_1.useViewportSize)();
    (0, react_1.useLayoutEffect)(() => {
        const buttonParent = buttonRef.current;
        /**
         * @description Check if any of the children are SVGs
         * @returns Boolean
         */
        const hasSVG = () => {
            if (buttonParent) {
                for (const child of buttonParent.childNodes) {
                    if (child.nodeName.toLowerCase() === "svg") {
                        return true;
                    }
                }
            }
            return false;
        };
        /**
         * @problem The button text has a 4px padding-(left|right) applied to it in the design. The button itself has a 8px padding around it. Unfortunately we can't simply apply a 12px padding around the button because it doesn't work when an icon is added. One of the main solution i've tried is using a combination css selectors :has|:not|(first|last)-child but it doesn't consider plain text as a child.
         * @solution Here we are checking whether a svg is amongst the children, if yes we loop through the children and pick out the plain text and wrap it in a span. This will then allow css selector to work.
         */
        if (buttonParent && hasSVG()) {
            const actualChildren = react_1.default.Children.map(children, (child) => {
                if (typeof child === "string") {
                    return react_1.default.createElement("span", {}, child);
                }
                return child;
            });
            setContent(actualChildren);
            return;
        }
        setContent(children);
    }, [children]);
    const getSpinnerColor = () => {
        if (typeof appearance === "object" &&
            (appearance[viewport.size] === "secondary" ||
                appearance[viewport.size] === "tertiary")) {
            return "blue";
        }
        if (appearance === "secondary" || appearance === "tertiary") {
            return "blue";
        }
        return "white";
    };
    const getSpinnerSize = () => {
        if (typeof size === "object") {
            return size[viewport.size] === "medium" ? "small" : "xsmall";
        }
        return size === "medium" ? "small" : "xsmall";
    };
    const getLoadingMessage = () => {
        if (typeof loadingMessage === "object") {
            return loadingMessage[viewport.size];
        }
        return loadingMessage;
    };
    const isLoadingMessage = getLoadingMessage() ? true : false;
    const isButtonFullWidth = () => {
        var _a;
        if (typeof fullwidth === "object") {
            return (_a = fullwidth[viewport.size]) !== null && _a !== void 0 ? _a : false;
        }
        return fullwidth;
    };
    const getLabel = () => {
        if (loading && loadingMessage) {
            if (typeof loadingMessage === "object") {
                return loadingMessage[viewport.size];
            }
            return loadingMessage;
        }
        if (loading && !loadingMessage) {
            return "Loading";
        }
        return props["aria-label"];
    };
    return (react_1.default.createElement(ButtonContext.Provider, { value: {
            buttonAppearance: appearance,
            buttonDisabled: disabled,
        } },
        react_1.default.createElement(Button_styles_1.StyledButton, Object.assign({ ref: (0, mergeRefs_1.mergeRefs)([buttonRef, forwardedRef]), "$appearance": appearance, "$size": size, "$loading": loading, "$loadingMessage": loadingMessage, "$isButtonFullWidth": isButtonFullWidth(), disabled: disabled || loading, "aria-label": getLabel(), type: "button" }, props), loading ? (react_1.default.createElement(react_1.default.Fragment, null,
            react_1.default.createElement(Button_styles_1.StyledButtonLoadingWrapper, { "$buttonSize": size },
                react_1.default.createElement(Spinner_1.Spinner, { size: getSpinnerSize(), color: getSpinnerColor() })),
            isLoadingMessage && (react_1.default.createElement(Box_1.Box, { as: "span" }, getLoadingMessage())))) : (content))));
});
const ButtonIcon = (0, react_1.forwardRef)((_a, forwardedRef) => {
    var { name, appearance = "outline", rotate = "0" } = _a, props = __rest(_a, ["name", "appearance", "rotate"]);
    const { buttonAppearance, buttonDisabled } = (0, react_1.useContext)(ButtonContext);
    return (react_1.default.createElement(Button_styles_1.StyledButtonIcon, Object.assign({ ref: forwardedRef, name: name, appearance: appearance, rotate: rotate, "$buttonAppearance": buttonAppearance, "$disabled": buttonDisabled }, props)));
});
ButtonRoot.displayName = "Button";
ButtonIcon.displayName = "Button.Icon";
exports.Button = Object.assign(ButtonRoot, {
    Icon: ButtonIcon,
});
//# sourceMappingURL=Button.js.map